import React, { useEffect, useState, useRef, useCallback } from "react";
import { Clock, ShieldCheck, Building2, Save, Check, AlertCircle, MousePointerClick, UserCheck } from "lucide-react";
import { ApiService } from "../../../services/api";
import DepartmentSelector from "../../../components/selectors/DepartmentSelector";
import { AppointmentAvailability } from "../../../types/appointment";
import { useAuth } from "../../../contexts/AuthContext";
import { useModal } from "../../../hooks/useModal";

interface DepartmentItem {
  id: string;
  name: string;
  color?: string;
}

interface TimeSlot {
  id: string;
  period: string;
  startTime: string;
  endTime: string;
}

interface SubTimeSlotAvailability {
  id: string;
  teacherId: string;
  day: string;
  timeSlotId: string;
  subTime: string;
  isAvailable: boolean;
}

const mockTimeSlots: TimeSlot[] = [
  { id: "1", period: "1. Saat", startTime: "08:00", endTime: "09:00" },
  { id: "2", period: "2. Saat", startTime: "09:00", endTime: "10:00" },
  { id: "3", period: "3. Saat", startTime: "10:00", endTime: "11:00" },
  { id: "4", period: "4. Saat", startTime: "11:00", endTime: "12:00" },
  { id: "5", period: "5. Saat", startTime: "12:00", endTime: "13:00" },
  { id: "6", period: "6. Saat", startTime: "13:00", endTime: "14:00" },
  { id: "7", period: "7. Saat", startTime: "14:00", endTime: "15:00" },
  { id: "8", period: "8. Saat", startTime: "15:00", endTime: "16:00" },
  { id: "9", period: "9. Saat", startTime: "16:00", endTime: "17:00" },
  { id: "10", period: "10. Saat", startTime: "17:00", endTime: "18:00" },
  { id: "11", period: "11. Saat", startTime: "18:00", endTime: "19:00" },
  { id: "12", period: "12. Saat", startTime: "19:00", endTime: "20:00" },
];

const days = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];

const trToEnDay: Record<string, string> = {
  Pazartesi: "monday",
  Salı: "tuesday",
  Çarşamba: "wednesday",
  Perşembe: "thursday",
  Cuma: "friday",
  Cumartesi: "saturday",
  Pazar: "sunday",
};

const enToTrDay: Record<string, string> = {
  monday: "Pazartesi",
  tuesday: "Salı",
  wednesday: "Çarşamba",
  thursday: "Perşembe",
  friday: "Cuma",
  saturday: "Cumartesi",
  sunday: "Pazar",
};

const addMinutes = (hhmm: string, minsToAdd: number): string => {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date(2000, 0, 1, h, m, 0, 0);
  d.setMinutes(d.getMinutes() + minsToAdd);
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  return `${hh}:${mm}`;
};

const toMinutes = (hhmm: string): number => {
  const [h, m] = String(hhmm).split(":").map(Number);
  return h * 60 + m;
};

const findParentTimeSlotId = (started: string): string => {
  const startedMin = toMinutes(started);
  const found = mockTimeSlots.find((ts) => {
    const s = toMinutes(ts.startTime);
    const e = toMinutes(ts.endTime);
    return startedMin >= s && startedMin < e;
  });
  return found ? String(found.id) : String(started);
};

export default function ProviderViewSettings() {
  const { user } = useAuth();
  const { showAlert } = useModal();
  const selectedTeacherId = String(user?.id || "me");

  const [autoApprove, setAutoApprove] = useState<boolean>(false);
  const [departments, setDepartments] = useState<DepartmentItem[]>([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
  const [selectedDeptValue, setSelectedDeptValue] = useState<string | null>(null);
  const [allowedDepartmentNames, setAllowedDepartmentNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const isInitialMount = useRef(true);

  const [initialAutoApprove, setInitialAutoApprove] = useState<boolean>(false);
  const [initialDepartmentIds, setInitialDepartmentIds] = useState<string[]>([]);

  const [slotDuration, setSlotDuration] = useState<number>(10);
  const [defaultCapacity, setDefaultCapacity] = useState<number>(1);
  const [isSavingConfig, setIsSavingConfig] = useState<boolean>(false);
  const [currentAvailability, setCurrentAvailability] = useState<SubTimeSlotAvailability[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [settings, depts, availabilityData] = await Promise.all([
          ApiService.getAppointmentSettings(),
          ApiService.getAllDepartments(null),
          ApiService.getAppointmentAvailability(),
        ]);

        const isAutoApprove = (settings as any)?.approvalMode === "AUTO";
        const allowed: string[] = ((settings as any)?.allowedDepartments || []).map((x: any) => String(x.id || x));

        const allowedDepartmentsWithNames = ((settings as any)?.allowedDepartments || []).reduce((acc: any, dept: any) => {
          if (dept.id && dept.name) {
            acc[dept.id] = dept.name;
          }
          return acc;
        }, {});

        setAutoApprove(isAutoApprove);
        setInitialAutoApprove(isAutoApprove);
        setSelectedDepartmentIds(allowed);
        setInitialDepartmentIds(allowed);
        setAllowedDepartmentNames(allowedDepartmentsWithNames);

        const deptData = ((depts as any)?.data ?? (depts as any) ?? []).map((d: any) => ({
          id: String(d.id),
          name: d.name || d.title || "",
          color: d.color,
        }));
        setDepartments(deptData);

        if (typeof (availabilityData as AppointmentAvailability)?.slotDuration === "number") {
          setSlotDuration((availabilityData as AppointmentAvailability).slotDuration);
        }
        if (typeof (availabilityData as AppointmentAvailability)?.defaultCapacity === "number") {
          setDefaultCapacity((availabilityData as AppointmentAvailability).defaultCapacity);
        }

        const uiSubs: SubTimeSlotAvailability[] = [];
        const weekly = (availabilityData as AppointmentAvailability)?.weekly || {};
        Object.entries(weekly).forEach(([engDay, ranges]: any) => {
          const trDay = enToTrDay[engDay] || engDay;
          Object.entries(ranges || {}).forEach(([range, slots]: any) => {
            (slots || []).forEach((slot: any) => {
              const timeSlotId = findParentTimeSlotId(slot.started);
              uiSubs.push({
                id: `${engDay}-${slot.started}`,
                teacherId: selectedTeacherId,
                day: trDay,
                timeSlotId: String(timeSlotId),
                subTime: slot.started,
                isAvailable: true,
              });
            });
          });
        });
        setCurrentAvailability(uiSubs);
      } catch (e: any) {
        setError(e?.message || "Ayarlar yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedTeacherId]);

  const handleAutoSave = useCallback(async () => {
    if (saving) return;

    try {
      setSaving(true);
      setError(null);
      await ApiService.updateAppointmentSettings({
        approvalMode: autoApprove ? "AUTO" : "MANUAL",
        allowedDepartments: selectedDepartmentIds,
      });

      setInitialAutoApprove(autoApprove);
      setInitialDepartmentIds(selectedDepartmentIds);
    } catch (e: any) {
      setError(e?.message || "Otomatik kayıt başarısız oldu");
    } finally {
      setSaving(false);
    }
  }, [saving, autoApprove, selectedDepartmentIds]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const autoApproveChanged = autoApprove !== initialAutoApprove;

    const sortedCurrent = [...selectedDepartmentIds].sort();
    const sortedInitial = [...initialDepartmentIds].sort();
    const departmentsChanged =
      sortedCurrent.length !== sortedInitial.length || sortedCurrent.some((id, index) => id !== sortedInitial[index]);

    if (autoApproveChanged || departmentsChanged) {
      handleAutoSave();
    }
  }, [autoApprove, selectedDepartmentIds, initialAutoApprove, initialDepartmentIds, handleAutoSave]);

  const toggleDepartment = (id: string) => {
    setSelectedDepartmentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleAddDepartment = (id: string, dept?: any) => {
    if (!id) return;
    setSelectedDepartmentIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    if (dept && dept.name) {
      setAllowedDepartmentNames((prev) => ({
        ...prev,
        [id]: dept.name,
      }));
    }
    setSelectedDeptValue(null);
  };

  const handleToggleAutoApprove = () => {
    setAutoApprove(!autoApprove);
  };

  const handleSaveConfig = async (newSlotDuration: number, newDefaultCapacity: number) => {
    setIsSavingConfig(true);
    try {
      const weekly: AppointmentAvailability["weekly"] = {} as any;
      days.forEach((trDay) => {
        const enDay = trToEnDay[trDay] || trDay;
        const dayEntries: Record<string, Array<{ started: string; ended: string; capacity?: number }>> = {};
        mockTimeSlots.forEach((slot) => {
          const selectedSubs = currentAvailability
            .filter((s) => s.day === trDay && s.timeSlotId === slot.id && s.isAvailable)
            .map((s) => s.subTime);
          if (selectedSubs.length > 0) {
            const rangeKey = `${slot.startTime}-${slot.endTime}`;
            if (!dayEntries[rangeKey]) dayEntries[rangeKey] = [];
            selectedSubs.forEach((st) => {
              dayEntries[rangeKey].push({
                started: st,
                ended: addMinutes(st, newSlotDuration),
              });
            });
          }
        });
        if (Object.keys(dayEntries).length > 0) {
          (weekly as any)[enDay] = dayEntries;
        }
      });

      const payload: AppointmentAvailability = {
        providerId: selectedTeacherId,
        weekly,
        defaultCapacity: newDefaultCapacity,
        workingHours: { start: "08:00", end: "20:00" },
        slotDuration: newSlotDuration,
        metadata: {},
      };

      await ApiService.updateAppointmentAvailability(payload);
      setSlotDuration(newSlotDuration);
      setDefaultCapacity(newDefaultCapacity);

      const availabilityData = await ApiService.getAppointmentAvailability();
      const uiSubs: SubTimeSlotAvailability[] = [];
      const weeklyReload = (availabilityData as AppointmentAvailability)?.weekly || {};
      Object.entries(weeklyReload).forEach(([engDay, ranges]: any) => {
        const trDay = enToTrDay[engDay] || engDay;
        Object.entries(ranges || {}).forEach(([range, slots]: any) => {
          (slots || []).forEach((slot: any) => {
            const timeSlotId = findParentTimeSlotId(slot.started);
            uiSubs.push({
              id: `${engDay}-${slot.started}`,
              teacherId: selectedTeacherId,
              day: trDay,
              timeSlotId: String(timeSlotId),
              subTime: slot.started,
              isAvailable: true,
            });
          });
        });
      });
      setCurrentAvailability(uiSubs);

      await showAlert({
        title: "Ayarlar Kaydedildi",
        message: "Zaman dilimi ve kapasite ayarlarınız başarıyla güncellendi.",
        type: "success",
      });
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error?.message || "Ayarlar kaydedilirken bir hata oluştu.",
        type: "error",
      });
    } finally {
      setIsSavingConfig(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-300 rounded-xl p-4 shadow-sm">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Randevu Yapılandırması</h2>
              <p className="text-sm text-gray-500">Zaman dilimleri ve kapasite ayarlarını buradan yönetebilirsiniz.</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-8">
          {/* Time Slot Duration Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-700">Zaman Dilimi Aralığı</label>
              <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100">
                {slotDuration} Dakika Seçili
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
              {[
                { value: 60, label: "60 dk" },
                { value: 30, label: "30 dk" },
                { value: 20, label: "20 dk" },
                { value: 15, label: "15 dk" },
                { value: 12, label: "12 dk" },
                { value: 10, label: "10 dk" },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSlotDuration(option.value)}
                  className={`group relative flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all duration-200 ${
                    slotDuration === option.value
                      ? "border-blue-600 bg-blue-50"
                      : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                  }`}>
                  <span className={`text-base font-bold mb-0.5 ${slotDuration === option.value ? "text-blue-700" : "text-gray-700"}`}>
                    {option.label}
                  </span>
                  {slotDuration === option.value && (
                    <div className="absolute top-1.5 right-1.5">
                      <div className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-start space-x-2 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg border border-gray-100">
              <AlertCircle className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
              <p>Zaman dilimi değişikliği yapıldığında, mevcut müsaitlik takviminiz seçilen yeni süreye göre yeniden hesaplanacaktır.</p>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Varsayılan Öğrenci Kapasitesi</label>
                <p className="text-sm text-gray-500">Her bir zaman dilimine aynı anda kaç öğrenci randevu alabilir?</p>
              </div>
              <div className="w-full sm:w-48">
                <div className="relative group">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={defaultCapacity}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setDefaultCapacity(Math.max(1, Math.min(50, val)));
                    }}
                    className="block w-full pl-4 pr-12 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                    <span className="text-gray-400 text-sm font-medium">Kişi</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2 border-t border-gray-100">
            <button
              onClick={() => handleSaveConfig(slotDuration, defaultCapacity)}
              disabled={isSavingConfig}
              className="flex items-center space-x-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-100 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm shadow-sm hover:shadow-md transform active:scale-[0.98]">
              {isSavingConfig ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Kaydediliyor...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Değişiklikleri Kaydet</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ShieldCheck className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Randevu Onay Tercihi</h2>
              <p className="text-sm text-gray-500">Gelen randevu taleplerinin nasıl işleneceğini belirleyin.</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => {
                if (!autoApprove) handleToggleAutoApprove();
              }}
              className={`relative p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                autoApprove
                  ? "border-blue-600 bg-blue-50 ring-1 ring-blue-600"
                  : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
              }`}>
              <div className="flex items-start justify-between mb-3">
                <div className={`p-2 rounded-lg ${autoApprove ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"}`}>
                  <Check className="w-5 h-5" />
                </div>
                {autoApprove && <div className="w-3 h-3 bg-blue-600 rounded-full animate-pulse" />}
              </div>
              <h3 className={`font-bold mb-1 text-base ${autoApprove ? "text-blue-900" : "text-gray-900"}`}>Otomatik Onay</h3>
              <p className="text-sm text-gray-500">Müsait zaman dilimlerine gelen talepler sistem tarafından anında onaylanır.</p>
            </button>

            <button
              onClick={() => {
                if (autoApprove) handleToggleAutoApprove();
              }}
              className={`relative p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                !autoApprove
                  ? "border-blue-600 bg-blue-50 ring-1 ring-blue-600"
                  : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
              }`}>
              <div className="flex items-start justify-between mb-3">
                <div className={`p-2 rounded-lg ${!autoApprove ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"}`}>
                  <MousePointerClick className="w-5 h-5" />
                </div>
                {!autoApprove && <div className="w-3 h-3 bg-blue-600 rounded-full animate-pulse" />}
              </div>
              <h3 className={`font-bold mb-1 text-base ${!autoApprove ? "text-blue-900" : "text-gray-900"}`}>Manuel Onay</h3>
              <p className="text-sm text-gray-500">Tüm randevu talepleri sizin onayınıza düşer. Siz onaylamadan kesinleşmez.</p>
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Building2 className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Departman Kısıtlaması</h2>
              <p className="text-sm text-gray-500">
                Sadece belirli departmanlardan randevu kabul etmek istiyorsanız buradan seçebilirsiniz.
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {loading && <p className="text-gray-500 mb-4 text-sm">Yükleniyor...</p>}

          <div className="mb-6">
            <DepartmentSelector
              value={selectedDeptValue || ""}
              onChange={handleAddDepartment}
              excludeIds={selectedDepartmentIds}
              allowClear={true}
              placeholder="Departman arayın ve eklemek için seçin..."
            />
          </div>

          <div className="bg-gray-50 rounded-lg p-4 min-h-[100px] border border-gray-200 border-dashed">
            {selectedDepartmentIds.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {selectedDepartmentIds.map((id) => {
                  const dept = departments.find((d) => d.id === id);
                  const name = allowedDepartmentNames[id] || dept?.name || id;
                  const color = dept?.color || "#3b82f6"; // Default blue-500

                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium shadow-sm transition-all group"
                      style={{
                        backgroundColor: `${color}20`, // 20% opacity
                        color: color,
                        border: `1px solid ${color}40`, // 40% opacity
                      }}>
                      <UserCheck className="w-3.5 h-3.5" style={{ color: color }} />
                      <span className="truncate">{name}</span>
                      <button
                        type="button"
                        onClick={() => toggleDepartment(id)}
                        className="transition-colors p-0.5 rounded-full hover:bg-black/5 ml-1"
                        aria-label="Kaldır"
                        style={{ color: color }}>
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 py-6">
                <Building2 className="w-12 h-12 opacity-10" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
